import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

interface Procedure {
  icon: string;
  title: string;
  description: string;
}

interface Advantage {
  icon: string;
  title: string;
  description: string;
}

interface ServiceLayoutProps {
  heroImage: string;
  breadcrumbKey: string;
  titleKey: string;
  descriptionKey: string;
  procedures: Procedure[];
  advantages: Advantage[];
  galleryImages: string[];
  ctaTitleKey: string;
  ctaDescriptionKey: string;
  ctaButtonKey: string;
}

export default function ServiceLayout({
  heroImage,
  breadcrumbKey,
  titleKey,
  descriptionKey,
  procedures,
  advantages,
  galleryImages,
  ctaTitleKey,
  ctaDescriptionKey,
  ctaButtonKey
}: ServiceLayoutProps) {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const scrollToServices = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('services');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={heroImage}
          alt={t(titleKey)}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-5xl font-bold mb-4">{t(titleKey)}</h1>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap">
              {t('servicePages.breadcrumb.home')}
            </Link>
            <span className="text-gray-400">/</span>
            {/* <span className="text-gray-600 whitespace-nowrap">{t('servicePages.breadcrumb.services')}</span> */}
            <Link to="/" onClick={() => scrollToServices()} className="text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap">
              {t('servicePages.breadcrumb.services')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 whitespace-nowrap">{t(breadcrumbKey)}</span>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed text-lg">
            {t(descriptionKey)}
          </p>
        </div>
      </div>

      {/* Procedures Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            {t('servicePages.procedures')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {procedures.map((procedure, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-4">
                  <i className={`${procedure.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {procedure.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {procedure.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Advantages Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            {t('servicePages.whyUs')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <div 
                key={index}
                className="text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-full mb-4 mx-auto">
                  <i className={`${advantage.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      {/*<div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            {t('servicePages.gallery')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                onClick={() => setSelectedImage(image)}
              >
                <div className="w-full h-80">
                  <img 
                    src={image}
                    alt={`${t('galleryImage')} ${index + 1}`}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-4xl text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>*/}

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors w-12 h-12 flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line"></i>
          </button>
          <img 
            src={selectedImage}
            alt="Gallery"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section */}
      <div className="py-16 bg-gradient-to-r from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('hero.cta')}
          </h2>
          <p className="text-xl text-teal-50 mb-8">
             {t(titleKey)}
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
             {t('hero.cta')}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}